import { formatCurrency } from '@if/core/utils'
import { Box, Divider, Typography } from '@mui/material'
import { companyConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { EPayoutRecoveryMethod, MerchantId } from '@src/types/Constants'
import { FundingSummaryDto } from '@src/types/FundingSummaryDto'
import { useTranslation } from 'react-i18next'
import RetentionBreakdown from './RetentionBreakdown'
import { useFormatFundingDate } from './shared'

interface Props {
  fundingSummary: FundingSummaryDto
}

const LoanInformation = ({ fundingSummary }: Props) => {
  const { t } = useTranslation()
  const config = companyConfigByFinancingProgramId(fundingSummary.financingProgramId)
  const formatFundingDate = useFormatFundingDate()

  const payments = fundingSummary.merchantPayments
  const merchantFee = parseFloat(fundingSummary.merchantFee) || 0
  const hasMerchantFee = merchantFee > 0
  const totalPayments = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0)
  const netAmount = totalPayments - merchantFee
  const { productsFundingDetails } = fundingSummary

  const isPayCustomer = (merchantId: string) =>
    merchantId === MerchantId.PayCustomerByBankTransfer.toString() ||
    merchantId === MerchantId.PayCustomerByCheque.toString()

  const getPaymentName = (payment: (typeof payments)[0]) => {
    if (isPayCustomer(payment.merchantId)) {
      return fundingSummary.customerNameProvince.split(' / ')[0]
    }
    return payment.merchantName ?? fundingSummary.merchantName
  }

  const getPaymentAmount = (payment: (typeof payments)[0]) => {
    if (productsFundingDetails?.payoutRecoveryMethod && payment.merchantId === fundingSummary.merchantId) {
      return productsFundingDetails.totalPayoutAmount
    }
    const amount = payment.amount ?? 0
    if (merchantFee > 0 && payment.merchantId === fundingSummary.merchantId) {
      return amount - merchantFee
    }
    return amount
  }

  const amountText = t(
    hasMerchantFee ? 'fundingSummary.amountConfirmationMerchantFee' : 'fundingSummary.amountConfirmation',
    {
      financingCompanyName: config.name,
      amount: formatCurrency(totalPayments),
      merchantFeeRate: fundingSummary.merchantFeeRate,
      date: formatFundingDate(fundingSummary.activationOn),
    },
  )

  return (
    <Box>
      <Divider sx={{ mb: 2, mt: 1, borderColor: 'primary.main' }} />

      <Box sx={{ marginLeft: 2, marginBottom: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {amountText}
        </Typography>

        {hasMerchantFee && (
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', ml: 1 }}>
            {t('fundingSummary.netAmount')} - {formatCurrency(netAmount)}
          </Typography>
        )}
        {payments.length > 1 &&
          payments.map((payment, idx) => (
            <Typography key={idx} variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', ml: 1 }}>
              {`${getPaymentName(payment)} - ${formatCurrency(getPaymentAmount(payment))}`}
            </Typography>
          ))}

        {productsFundingDetails?.payoutRecoveryMethod === EPayoutRecoveryMethod.Retention && (
          <RetentionBreakdown productsFundingDetails={productsFundingDetails} />
        )}

        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
          {t('fundingSummary.postDateDisbursement')}
          {productsFundingDetails?.payoutRecoveryMethod === EPayoutRecoveryMethod.Chargeback &&
            productsFundingDetails.chargebackEndDate &&
            ` ${t('fundingSummary.chargebackNotice', {
              date: formatFundingDate(productsFundingDetails.chargebackEndDate),
            })}`}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2, borderColor: 'primary.main' }} />
    </Box>
  )
}

export default LoanInformation
